/**
 * The inbox domain's client-side vocabulary, and the one flow the screen cannot
 * express as a plain `useApiQuery`: the **access-reason-gated download** of an
 * inbound attachment.
 *
 * This mirrors `recruiting/candidate.ts`'s own download half exactly — the same
 * shape `hr/employee-document.ts` copies for its own surface — because the inbox
 * attachment download is the same act as the candidate-CV download: a `fetch` with
 * `X-Access-Reason` whose `302` the browser follows to a signed GET, made through
 * `api/client.ts`'s own `sensitiveDownload` because the shared `request` refuses
 * redirects. Keeping the orchestration here, behind a small pure-ish function, is
 * what makes it testable without a DOM (the screen injects the one side effect it
 * has — `deliverBlobDownload`, reused rather than re-implemented, because the
 * browser side effect is generic and does not know about candidates or inboxes).
 *
 * ── The reason is checked here before a request goes out ────────────────────
 *
 * `reason.ts`'s whole argument: a screen that fires a request it knows will be
 * refused teaches people that refusals are noise. So `downloadInboxAttachment`
 * validates the reason with `isUsableReason` and, when it is missing or too short,
 * returns the `WORKFORCE_ACCESS_REASON_REQUIRED` refusal **without a request** —
 * the same code the server would answer, which `ApiErrorNote` renders as the
 * permission-denied state. Fail closed, and learn the rule from the field.
 *
 * ── A rejected/unstored attachment is never offered as a download ───────────
 *
 * That rule is enforced in the screen, not here: `InboxAttachment.stored` decides
 * whether a Download button exists at all for a given row, so this function is
 * never called against one that is `stored: false`. It is stated here anyway
 * because it is the fact this whole module exists to serve: a `409
 * WORKFORCE_CONFLICT` from the server would mean the row and its metadata are
 * real but there were never any bytes to redirect to — a screen defect this
 * client-side gate exists to make unreachable in the first place.
 */

import type { ApiClient } from '../api/client';
import { CLIENT_TRANSPORT_FAILED, WorkforceApiError } from '../api/errors';
import type { MessageKey } from '../i18n/messages';
import {
  inboxAttachmentDownloadPath,
  type InboxAttachment,
  type InboxParseStatus,
} from '../api/inbox';
import { isUsableReason } from '../access/reason';
// The browser side effect and its type are generic; reused rather than re-implemented.
import { deliverBlobDownload, type DeliverBlob } from './candidate';
import type { Severity } from '../states/icons';

export { deliverBlobDownload, type DeliverBlob };

/** The name of a message's `parse_status`, for the list row and the detail. */
export const PARSE_STATUS_LABEL: Readonly<Record<InboxParseStatus, MessageKey>> = {
  parsed: 'inbox.parseStatus.parsed',
  partial: 'inbox.parseStatus.partial',
  needs_human: 'inbox.parseStatus.needs_human',
};

/**
 * The severity/icon each `parse_status` reads as, icon + text per `DESIGN.md` §4.
 * `parsed` is the quiet, low-severity case; `partial` is a caution; `needs_human`
 * is the loudest — the parser gave up and a person has to look.
 */
export const PARSE_STATUS_SEVERITY: Readonly<Record<InboxParseStatus, Severity>> = {
  parsed: 'low',
  partial: 'medium',
  needs_human: 'high',
};

/* ─────────────────────────── the access-reason-gated attachment download ── */

/** What a screen may remember about a download it asked for. Deliberately holds no URL. */
export type DownloadState =
  | { readonly status: 'idle' }
  | { readonly status: 'requesting'; readonly attachmentId: string }
  | { readonly status: 'delivered'; readonly attachmentId: string; readonly filename: string }
  | {
      readonly status: 'refused';
      readonly attachmentId: string;
      readonly error: WorkforceApiError;
    };

/**
 * The `403 WORKFORCE_ACCESS_REASON_REQUIRED` this client raises **itself** when the
 * reason is missing or too short — the same code the server answers, so
 * `ApiErrorNote` renders the same permission-denied state whichever raised it. The
 * message is never shown (`PermissionDenied` keys off the code), but it is a true
 * sentence anyway.
 */
export function accessReasonRefusal(): WorkforceApiError {
  return new WorkforceApiError({
    code: 'WORKFORCE_ACCESS_REASON_REQUIRED',
    message: 'An access reason of at least ten characters is required to open an attachment.',
    status: 403,
  });
}

/** A filename to show when the server recorded none. */
const UNNAMED_FALLBACK = 'attachment';

/**
 * Open one inbound attachment, on the record.
 *
 * The reason is validated **before** a request goes out; a bad one is refused here
 * with no `wp_data_access_logs` row possible, because none is written for a request
 * that was never made. A good one is sent — trimmed, as the server trims before
 * measuring — as `X-Access-Reason`; the browser follows the `302` and the bytes are
 * handed to `deliver`. The signed URL never reaches this function.
 */
export async function downloadInboxAttachment(
  client: Pick<ApiClient, 'sensitiveDownload'>,
  messageId: string,
  attachment: InboxAttachment,
  reason: string,
  deliver: DeliverBlob,
): Promise<DownloadState> {
  if (!isUsableReason(reason)) {
    return { status: 'refused', attachmentId: attachment.id, error: accessReasonRefusal() };
  }
  const filename = attachment.filename ?? UNNAMED_FALLBACK;
  try {
    const blob = await client.sensitiveDownload(
      inboxAttachmentDownloadPath(messageId, attachment.id),
      reason.trim(),
    );
    deliver(blob, filename);
    return { status: 'delivered', attachmentId: attachment.id, filename };
  } catch (cause) {
    return {
      status: 'refused',
      attachmentId: attachment.id,
      error: asWorkforceError(cause, 'download failed'),
    };
  }
}

function asWorkforceError(cause: unknown, fallback: string): WorkforceApiError {
  return cause instanceof WorkforceApiError
    ? cause
    : new WorkforceApiError({
        code: CLIENT_TRANSPORT_FAILED,
        message: cause instanceof Error ? cause.message : fallback,
        status: 0,
      });
}
